/**
 * CAOS-606 — the workspace's half of "Viewing as": reading which roles this person may preview
 * as, and asking the kernel to start or stop previewing as one.
 *
 * There is nothing here that decides anything. The kernel owns the preview entirely — it lives on
 * the session row, it is verified against the sets the person actually holds, and it narrows only
 * what is visible. This module is the read and the two writes, kept in one place so the shell can
 * ask one question and the account menu can perform one act.
 *
 * Same-origin, and fail-soft in one direction only. A failed read returns `null` — "could not
 * ask" — which is NOT the same answer as a successful "nothing is being previewed", and the type
 * makes the caller say which one it is handling.
 *
 * The difference matters more here than the usual fail-soft reasoning suggests. The kernel resolves
 * the preview from the SESSION ROW, so it goes on narrowing every data answer whether or not this
 * read succeeded. Folding a failure into "not previewing" therefore paints the full, unnarrowed
 * workspace with no banner over data that is still narrowed — a person then draws confident, wrong
 * conclusions about what a colleague can see, with nothing on screen to warn them. `main.ts` falls
 * back to the active role `auth whoami` reports, so the banner survives this read failing.
 */

/** The control-plane routes behind the host `/api/*` proxy (which injects the session bearer). */
export const PREVIEW_STATE_PATH = '/api/preview/state';
export const PREVIEW_START_PATH = '/api/preview/start';
export const PREVIEW_END_PATH = '/api/preview/end';

/** A permission set the signed-in person holds, offered in the account menu's switcher. */
export interface PreviewRoleOption {
  readonly key: string;
  readonly label: string;
}

/** What the workspace needs to know about previewing, for one render. */
export interface PreviewState {
  /** Every role on offer. Empty ⇒ the switcher is not drawn at all (the component hides it). */
  readonly roles: readonly PreviewRoleOption[];
  /** The role in force, or null when the workspace is resolving normally. */
  readonly active: PreviewRoleOption | null;
  /**
   * Object keys visible under the active role, or null when none is active.
   *
   * Null is not an empty list, and the shell acts differently on each: null means "hide nothing",
   * an empty array means "previewing as a role that reaches no object". Collapsing the two would
   * either hide every tab from somebody who is not previewing, or show every tab to somebody
   * previewing as a role with no grants at all — and the second is the failure that makes a
   * preview worse than useless, because it tells you a colleague can see something they cannot.
   */
  readonly visibleObjects: readonly string[] | null;
  /** App keys the active role administers (the per-app Settings gate), or null when not previewing. */
  readonly administers: readonly string[] | null;
}

/** The state a workspace renders when it has no answer: not previewing, nothing on offer. */
export const NO_PREVIEW: PreviewState = { roles: [], active: null, visibleObjects: null, administers: null };

/** A minimal response shape, so a test stub need not build a whole `Response`. */
export interface PreviewFetchResponse {
  json(): Promise<unknown>;
}

/** The fetch seam — same-origin, never cached, behind the host BFF proxy. */
export type PreviewFetch = (path: string, init?: { method?: string; body?: string }) => Promise<PreviewFetchResponse>;

const defaultPreviewFetch: PreviewFetch = (path, init) =>
  fetch(path, {
    method: init?.method ?? 'GET',
    headers: { accept: 'application/json', ...(init?.body ? { 'content-type': 'application/json' } : {}) },
    credentials: 'same-origin',
    cache: 'no-store',
    ...(init?.body ? { body: init.body } : {}),
  }) as unknown as Promise<PreviewFetchResponse>;

let previewFetch: PreviewFetch = defaultPreviewFetch;

/** Override the fetch seam (production wiring / tests). */
export function setPreviewFetch(fn: PreviewFetch): void {
  previewFetch = fn;
}

/**
 * Read one `{ ok, data }` envelope into a {@link PreviewState}, or `null` when it carries no
 * answer — an error envelope is the kernel REFUSING, which is not the kernel saying that nothing
 * is being previewed, and the two must not collapse into each other here.
 */
function readState(payload: unknown): PreviewState | null {
  const env = payload as { ok?: boolean; data?: unknown } | null;
  if (!env || env.ok !== true || env.data == null || typeof env.data !== 'object') return null;
  const data = env.data as Record<string, unknown>;

  const roles: PreviewRoleOption[] = [];
  if (Array.isArray(data['roles'])) {
    for (const raw of data['roles']) {
      if (raw == null || typeof raw !== 'object') continue;
      const r = raw as Record<string, unknown>;
      // A role with no key cannot be switched to, and one with no label cannot be named in a menu.
      // Either way it is dropped rather than rendered as a blank row somebody can choose.
      if (typeof r['key'] === 'string' && r['key'] && typeof r['label'] === 'string' && r['label']) {
        roles.push({ key: r['key'], label: r['label'] });
      }
    }
  }

  const rawActive = data['active'];
  let active: PreviewRoleOption | null = null;
  if (rawActive != null && typeof rawActive === 'object') {
    const a = rawActive as Record<string, unknown>;
    if (typeof a['key'] === 'string' && a['key']) {
      active = { key: a['key'], label: typeof a['label'] === 'string' && a['label'] ? a['label'] : a['key'] };
    }
  }

  // Read the two narrowing lists ONLY when a role is in force. An answer carrying a list with no
  // active role is incoherent, and honouring the list would hide tabs from somebody the workspace
  // has just been told is not previewing.
  const strings = (v: unknown): string[] | null =>
    active && Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : null;

  return {
    roles,
    active,
    visibleObjects: strings(data['visibleObjects']),
    administers: strings(data['administers']),
  };
}

/**
 * Read the current preview state, or `null` when the kernel could not be asked.
 *
 * Never throws, and never answers "nothing is being previewed" on behalf of a kernel that did not
 * say so. A caller that treats `null` as {@link NO_PREVIEW} has made that decision explicitly.
 */
export async function fetchPreviewState(): Promise<PreviewState | null> {
  try {
    const res = await previewFetch(PREVIEW_STATE_PATH);
    return readState(await res.json());
  } catch {
    return null;
  }
}

/**
 * The role in force, reconciling the two independent reads of that one fact.
 *
 * `read` is {@link fetchPreviewState}'s answer — `null` when the kernel could not be asked.
 * `fromIdentity` is what `auth whoami` reported on the call the boot already makes.
 *
 * A read that SUCCEEDED wins outright, including when it says nothing is being previewed: it is the
 * fresher and more specific answer, and letting a racing whoami overrule it would put a banner over
 * a workspace that has just been told, authoritatively, that it is not previewing. whoami is
 * consulted only when there is no answer at all — which is the case this exists for, because the
 * kernel goes on narrowing from the session row whether or not the host managed to ask.
 */
export function activePreviewFrom(
  read: PreviewState | null,
  fromIdentity?: PreviewRoleOption,
): PreviewRoleOption | null {
  if (read) return read.active;
  return fromIdentity ?? null;
}

/**
 * Begin previewing as `roleKey`, or stop when it is null. Resolves to `true` when the kernel
 * accepted the change.
 *
 * The caller reloads on `true`. Not a partial repaint: the apps, tabs, page layouts and every
 * record already on screen were resolved under the PREVIOUS role, so re-rendering the chrome alone
 * would leave a workspace half in each — which is exactly the incoherent middle state this feature
 * cannot afford, since a person is looking at it specifically to learn what somebody else sees.
 */
export async function setPreviewRole(roleKey: string | null): Promise<boolean> {
  try {
    const res = await previewFetch(
      roleKey === null ? PREVIEW_END_PATH : PREVIEW_START_PATH,
      { method: 'POST', ...(roleKey === null ? {} : { body: JSON.stringify({ role: roleKey }) }) },
    );
    const env = (await res.json()) as { ok?: boolean } | null;
    return env?.ok === true;
  } catch {
    return false;
  }
}
